import { useCallback, useState } from 'react'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useDisclosure } from '@mantine/hooks'
import { useNavigate, useParams } from 'react-router-dom'
import isString from 'lodash/isString'
import { useTranslation } from 'react-i18next'
import { ArrayParam, NumberParam, QueryParamConfig, StringParam, useQueryParams, withDefault } from 'use-query-params'

import { useDonationStatusFilter } from '../hooks/useDonationStatusFilter'
import { DonationDrawer } from '../DonationDrawer/DonationDrawer'
import { useDonationTypeFilter } from '../hooks/useDonationTypeFilter'
import { RoutePath } from '../../layout/Routes'
import { useDonationCategoryFilter } from '../hooks/useDonationCategoryFilter'

import {
  Badge,
  Box,
  Button,
  DateRange,
  FilterList,
  Flex,
  Icon,
  Menu,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableColumnSortDirection,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip,
  Typography
} from '@percent/lemonade'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import {
  POSTHOG_FEATURE_FLAGS,
  useAuthState,
  useDidMountEffect,
  usePostHogFeatureFlag,
  useQueryList,
  useSearchFilter
} from '@percent/partner-dashboard/common/hooks'
import { useCSVDownloader } from '@percent/partner-dashboard/common/hooks/useCSVDownloader/useCSVDownloader'
import { dayJS, getFormattedDate } from '@percent/partner-dashboard/common/library/utility/date'
import { fileNameFunc } from '@percent/partner-dashboard/common/library/utility/utility'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import {
  Donation,
  DonationListStatusFilterEnum,
  DonationType
} from '@percent/partner-dashboard/services/donation/donationService.types'
import { formatAmount, formatMinorUnitsValueToFullNumberWithDecimals } from '@percent/utility'
import { DonationStatusTableCell } from './DonationStatusTableCell'
import {
  defaultStatus,
  donationCategoryTranslations,
  donationTypeTranslations,
  initialDirection
} from './DonationListTable.types'
import { getDisplayDonationStatus } from '@percent/partner-dashboard/common/library/utility/getDisplayDonationStatus'
import styles from './DonationListTable.module.scss'

const pageSizeOptions = [
  { label: '10', value: '10' },
  { label: '20', value: '20' },
  { label: '50', value: '50' },
  { label: '100', value: '100' }
]

const DEFAULT_EXPAND = ['disbursement', 'organisation', 'walletFundingSources']

export function DonationListTable() {
  const {
    donationService,
    reportingService: { getDonationReport }
  } = useServices()
  const { t } = useTranslation()
  const [isDatePickerOpened, setIsDatePickerOpened] = useState(false)
  const [selectedDonation, setSelectedDonation] = useState<Donation | undefined>()
  const { fundraiserId } = useParams<{ fundraiserId: string }>()
  const [queryParams, setQueryParams] = useQueryParams({
    pageSize: withDefault(NumberParam, 10),
    startDate: StringParam,
    endDate: StringParam,
    status: ArrayParam,
    kinds: ArrayParam,
    category: ArrayParam,
    expand: withDefault(ArrayParam, DEFAULT_EXPAND),
    direction: StringParam as QueryParamConfig<TableColumnSortDirection | null | undefined>,
    query: withDefault(StringParam, undefined),
    fundraiserId: withDefault(StringParam, fundraiserId || undefined)
  })
  const navigate = useNavigate()

  const [opened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false)

  const [direction, setDirection] = useState<TableColumnSortDirection>(
    (queryParams.direction as TableColumnSortDirection) || initialDirection
  )
  const [sortByColumn, setSortByColumn] = useState<string | undefined>(queryParams.direction ? 'createdAt' : undefined)

  const handleRequestSort = () => {
    const newDirection = direction === 'ASC' ? 'DESC' : 'ASC'

    setDirection(newDirection)
    setSortByColumn('createdAt')
    setQueryParams({ direction: newDirection })
  }

  const [{ data, totalResults, isLoading, errorMessage }, { query, nextPage, previousPage }] = useQueryList(
    donationService.getDonationList,
    {
      ...queryParams,
      status: queryParams.status?.filter(isString) || defaultStatus,
      kinds: queryParams.kinds?.filter(isString) || undefined,
      category: queryParams.category?.filter(isString) || undefined
    }
  )

  const { currencyInfo, partner } = useAuthState()

  useDidMountEffect(
    () =>
      query({
        ...queryParams,
        status: queryParams.status?.filter(isString) || defaultStatus,
        kinds: queryParams.kinds?.filter(isString) || undefined,
        category: queryParams.category?.filter(isString) || undefined
      }),
    [queryParams, query]
  )

  const [{ loading: csvDownloaderLoading, errorCSVMessage }, { fetchDonationCSV }] = useCSVDownloader({
    service: getDonationReport,
    fileName: fileNameFunc,
    successMessage: t('toast.donationList.successMessage')
  })

  const handleClick = () => {
    setIsDatePickerOpened(prev => !prev)
  }

  const handleExport = async () => {
    if (fetchDonationCSV) {
      await fetchDonationCSV({
        startDate:
          (queryParams.startDate && dayJS(queryParams.startDate).utc().toDate()) || dayJS('2022-10-18').utc().toDate(),
        endDate: dayJS(queryParams.endDate).endOf('day').toDate(),
        status: (queryParams.status?.filter(isString) || defaultStatus) as DonationListStatusFilterEnum[],
        kinds: queryParams.kinds as DonationType[],
        query: queryParams.query,
        fundraiserId: queryParams.fundraiserId
      })
    }
  }

  const handleClearDate = useCallback(() => {
    setQueryParams({ startDate: undefined, endDate: undefined })
  }, [setQueryParams])

  const hasDonorData = data && data.length > 0 && data[0].email !== undefined
  const donorColumn = hasDonorData
    ? [{ id: 'donor', content: t('table.header.donor'), isSortable: false, props: { width: '20%' } }]
    : []

  const columnsHeaders: TableColumn[] = [
    { id: 'donation', content: t('table.header.donation'), isSortable: false, props: { width: '10%' } },
    { id: 'recepient', content: t('table.header.organisation'), isSortable: false, props: { width: '20%' } },
    ...donorColumn,
    { id: 'type', content: t('table.header.type'), isSortable: false, props: { width: '10%' } },
    { id: 'category', content: t('table.header.category'), isSortable: false, props: { width: '10%' } },
    { id: 'status', content: t('table.header.status'), isSortable: false, props: { width: '12%' } },
    { id: 'createdAt', content: t('table.header.donationCreated'), isSortable: true, props: { width: '13%' } },
    { id: '', content: '', isSortable: false, props: { width: '3%' } }
  ]

  const dateRange = queryParams.startDate && queryParams.endDate

  const { searchValue, handleChange, handleKeyPress, handleClearValue } = useSearchFilter({
    queryParams,
    setQueryParams
  })

  const { donationKindOptions, onKindChange, selectedKinds } = useDonationTypeFilter({
    queryParams,
    setQueryParams
  })

  const { donationCategoryOptions, onCategoryChange, selectedCategories } = useDonationCategoryFilter({
    queryParams,
    setQueryParams
  })

  const { donationStatusOptions, onStatusChange, selectedStatus } = useDonationStatusFilter({
    queryParams,
    setQueryParams
  })

  const isCategoryFilterEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_DONATION_CATEGORY_FILTER)

  if (errorMessage || errorCSVMessage) {
    return <ErrorView errorMessage={errorMessage || errorCSVMessage} />
  }

  return (
    <TableProvider
      columns={columnsHeaders}
      totalResults={totalResults}
      isLoading={isLoading}
      tableHeadTestId="new-donation-list-table-head"
      illustration="no-results"
      emptyTableTitle={t('typography.noDonationTitle')}
      emptyTableDescription={t('typography.noDonationDescription')}
      sortDirection={direction}
      sortByColumn={sortByColumn}
      handleRequestSort={handleRequestSort}
    >
      <div className={styles.actionWrapper}>
        <SearchInput
          name="search"
          value={searchValue}
          onKeyDown={handleKeyPress}
          onChange={handleChange}
          handleClearValue={handleClearValue}
          placeholder={(hasDonorData && t('table.donationListSearchPlaceholder')) || ''}
          status={searchValue.length >= 255 ? 'danger' : 'default'}
          statusMessage={t('errorMessage.shouldNotExceed255')}
          data-testid="donation-list-search-input"
        />
        <Flex align="center" ml={8} mr={4} h="2.5rem" gap={8}>
          {isCategoryFilterEnabled && (
            <FilterList
              label={t('table.header.category')}
              options={donationCategoryOptions}
              selectedValues={selectedCategories}
              onChange={onCategoryChange}
              testId="donation-category-filter"
            />
          )}
          <FilterList
            label={t('table.header.type')}
            options={donationKindOptions}
            selectedValues={selectedKinds}
            onChange={onKindChange}
            testId="donation-kind-filter"
          />
          <FilterList
            label={t('table.header.status')}
            options={donationStatusOptions}
            selectedValues={selectedStatus}
            onChange={onStatusChange}
            testId="donation-status-filter"
          />
          <DateRange
            open={isDatePickerOpened}
            setOpen={setIsDatePickerOpened}
            setQueryParams={setQueryParams}
            queryParams={queryParams}
            maxDate={new Date()}
            data-testid="donation-list-date-range"
          >
            <button onClick={handleClick} className={styles.dateButton} type="button">
              <Flex align="center" gap={4}>
                <span>
                  {(dateRange &&
                    `${dayJS(queryParams.startDate).format('ll')} - ${dayJS(queryParams.endDate).format('ll')}`) ||
                    t('button.dateAll')}
                </span>
                <Icon name={isDatePickerOpened ? 'chevron-up' : 'chevron-down'} size={3} color="black" />
              </Flex>
            </button>
          </DateRange>
          {dateRange && (
            <Typography className={styles.clearText} onClick={handleClearDate}>
              {t('typography.clearDate')}
            </Typography>
          )}
        </Flex>
        <Flex gap={8}>
          {csvDownloaderLoading ? (
            <Button variant="secondary" data-testid="export-csv-loading">
              {t('button.exporting')}
            </Button>
          ) : (
            <Button variant="secondary" data-testid="export-csv-btn" onClick={handleExport}>
              {t('button.exportCsv')}
            </Button>
          )}
          {!fundraiserId && (
            <Button
              variant="primary"
              data-testid="create-corporate-donation-btn"
              onClick={() => {
                navigate(RoutePath.CORPORATE_DONATION)
              }}
              startIcon="add"
            >
              {t('button.corporateDonation')}
            </Button>
          )}
        </Flex>
      </div>
      <Table data-testid="donation-list-table">
        <TableBody>
          {data?.map(
            (
              {
                id,
                amount,
                status,
                userId,
                createdAt,
                currencyCode,
                firstName,
                lastName,
                email,
                organisation,
                type,
                category,
                disbursement,
                walletFundingSources
              }: Donation,
              index: number
            ) => {
              const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName
              const donorName = fullName || email || userId
              const fundingSourceNames = walletFundingSources?.flatMap(source => source.name ?? []) ?? []

              return (
                <TableRow key={id} data-testid="donation-list-row">
                  <TableCell>
                    {formatAmount({
                      currencyCode,
                      value: formatMinorUnitsValueToFullNumberWithDecimals(
                        amount,
                        currencyInfo?.find(el => el.code === currencyCode)?.minorUnits as number
                      )
                    })}{' '}
                    {currencyCode}
                  </TableCell>
                  <TableCell>{organisation?.data?.name}</TableCell>
                  {hasDonorData && (
                    <TableCell>
                      {[DonationType.Matched, DonationType.Direct].includes(type) ? partner?.name : donorName}
                    </TableCell>
                  )}
                  <TableCell>
                    <Flex align="center" gap={4}>
                      <Badge variant="default">{t(donationTypeTranslations[type])}</Badge>
                      {fundingSourceNames.length > 0 && (
                        <Tooltip
                          content={
                            fundingSourceNames.length === 1
                              ? t('toolTip.workplaceGiving.fundingSource', {
                                  fundingSourceName: fundingSourceNames[0]
                                })
                              : t('toolTip.workplaceGiving.fundingSources', {
                                  fundingSourceNames: fundingSourceNames.join(', ')
                                })
                          }
                        >
                          <span className={styles.fundingSourceIcon} data-testid="donation-funding-source-info">
                            <Icon name="info" size={5} color="gray400" />
                          </span>
                        </Tooltip>
                      )}
                    </Flex>
                  </TableCell>
                  <TableCell>
                    <Badge variant="default">{t(donationCategoryTranslations[category ?? 'unclassified'])}</Badge>
                  </TableCell>
                  <TableCell className={styles.statusCell}>
                    <DonationStatusTableCell status={getDisplayDonationStatus({ disbursement, status })} />
                  </TableCell>
                  <TableCell>{getFormattedDate(createdAt)}</TableCell>

                  <TableCell className={styles.ellipsesCell}>
                    <Menu
                      title={<Icon name="menu-vertical" size={6} />}
                      sections={[
                        {
                          items: [
                            {
                              key: 'view-donation-details',
                              label: 'View details'
                            }
                          ]
                        }
                      ]}
                      onSelect={() => {
                        setSelectedDonation(data[index])
                        openDrawer()
                      }}
                    />
                  </TableCell>
                </TableRow>
              )
            }
          )}
          {selectedDonation && (
            <DonationDrawer onOpen={openDrawer} opened={opened} onClose={closeDrawer} donation={selectedDonation} />
          )}
        </TableBody>
      </Table>
      <TablePagination
        totalResultsText={t('table.results', { count: totalResults || undefined })}
        previousPage={previousPage}
        nextPage={nextPage}
        data-testid="donation-list-pagination"
        rightContent={
          <Flex direction="row" align="center" gap={8}>
            <Typography>{t('typography.resultsOnPage')}</Typography>
            <Box style={{ width: '100px' }}>
              <Select
                name="pageSize"
                onChange={option => {
                  if (option?.value) {
                    setQueryParams({ ...queryParams, pageSize: Number(option.value) })
                  }
                }}
                defaultValue={pageSizeOptions.find(opt => String(opt.value) === String(queryParams.pageSize))}
                options={pageSizeOptions}
                orientation="up"
                data-testid="donation-list-page-size-select"
              />
            </Box>
          </Flex>
        }
      />
    </TableProvider>
  )
}
